import React, { useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BRAND } from "../config/brand";
import { themeNumber } from "../pages/home/shared";
import AppIcon from "./AppIcon";
import AppNavbar from "./AppNavbar";
import Footer from "./Footer";

// Keep the ten sites' specialties visible beside the shared app experience.
const STORIES = {
  1: ["문서가 많은 사무실에", "꼼꼼한 선택,", "든든한 문서 파트너.", "보안 문서부터 스캔 보관까지. 법무·세무·행정 업무에 맞는 복합기를 함께 찾아보세요."],
  2: ["수업 준비가 한결 가볍도록", "교재부터 시험지까지,", "출력을 더 편하게.", "출력이 몰리는 시간에도 여유롭게. 교재의 컬러 비율과 출력량에 맞춰 비교해 보세요."],
  3: ["우리 회사의 다음 시작을 위해", "필요한 만큼,", "합리적인 프린팅.", "초기 비용부터 매달 나가는 운영비까지. 지금 사무실 규모에 맞는 선택을 도와드립니다."],
  4: ["함께 쓰는 사무실의 새로운 기준", "공간은 다르게,", "출력은 스마트하게.", "공유오피스부터 여러 지점까지. 사용자와 동선을 고려한 출력 환경을 만나보세요."],
  5: ["매일의 업무가 편안하도록", "문서 업무에도", "세심한 케어를.", "접수부터 행정 업무까지. 소모품과 유지보수 조건을 함께 살펴보고 선택하세요."],
  6: ["사무실의 믿음직한 동료", "반복되는 출력,", "한결 가벼운 업무.", "송장과 거래명세서부터 일상 문서까지. 우리 팀에 필요한 장비를 비교해 보세요."],
  7: ["아이디어가 선명하게 전해지도록", "좋은 생각을,", "좋은 출력으로.", "도면과 제안서에 맞는 컬러와 A3 출력. 작업 방식에 어울리는 복합기를 찾아보세요."],
  8: ["현장의 일정에 맞춰", "시작부터 철수까지,", "필요한 기간만큼.", "프로젝트 사무실과 건설 현장을 위한 렌탈. 사용 기간과 설치 환경부터 상담합니다."],
  9: ["많은 출력도 안정적으로", "팀의 속도에 맞는", "든든한 복합기.", "부서별 사용량부터 유지보수까지. 대량 출력에 필요한 조건을 함께 비교합니다."],
  10: ["지금 필요한 출력을 위해", "필요한 그 순간,", "바로 쓸 수 있도록.", "행사와 임시 사무실, 갑작스러운 교체까지. 필요한 날짜와 설치 가능 일정을 확인하세요."],
};

function BrandPanel() {
  const [eyebrow, line1, line2, description] = STORIES[themeNumber()] || STORIES[3];
  return (
    <aside className="clone-brand-panel" aria-label={`${BRAND.name} 안내`}>
      <div className="clone-brand-content">
        <Link to="/" className="clone-brand-sign" aria-label={`${BRAND.name} 홈`}>
          <span className="clone-brand-symbol"><AppIcon name="printer" size={30} /></span>
          <span>{BRAND.logo}<small>OFFICE PRINTING PARTNER</small></span>
        </Link>
        <p className="clone-brand-eyebrow">{eyebrow}</p>
        <h2>{line1}<br /><em>{line2}</em></h2>
        <p className="clone-brand-description">{description}</p>
        <div className="clone-brand-actions">
          <Link to="/rental"><AppIcon name="printer" size={21} /> 우리 사무실 복합기 찾기 <AppIcon name="arrow" size={19} /></Link>
          <Link to="/consultation?tab=rental"><AppIcon name="chat" size={21} /> 맞춤 견적 상담하기 <AppIcon name="arrow" size={19} /></Link>
        </div>
        <div className="clone-brand-contact">
          <a href={`tel:${BRAND.phone}`}><AppIcon name="phone" size={20} />{BRAND.phone}</a>
          <span>평일 09:00–18:00</span>
        </div>
        <p className="clone-brand-footnote">복합기 렌탈 · 토너 · 유지보수</p>
      </div>
    </aside>
  );
}

function BottomNav() {
  const items = [
    ["/", "홈", "home"], ["/store", "스토어", "store"],
    ["/rental", "렌탈", "printer"], ["/compare", "비교", "compare"],
    ["/consultation", "상담", "chat"],
  ];
  return <nav className="clone-bottom-nav" aria-label="앱 하단 메뉴">
    {items.map(([to, label, icon]) => <NavLink key={to} to={to} end={to === "/"}>
      <AppIcon name={icon} size={23} /><span>{label}</span>
    </NavLink>)}
  </nav>;
}

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const shellRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const panel = panelRef.current;
    const shell = shellRef.current;
    const syncBounds = () => {
      const { left, width } = panel.getBoundingClientRect();
      shell.style.setProperty("--clone-panel-left", `${left}px`);
      shell.style.setProperty("--clone-panel-width", `${width}px`);
    };
    syncBounds();
    const observer = new ResizeObserver(syncBounds);
    observer.observe(panel);
    window.addEventListener("resize", syncBounds);
    return () => { observer.disconnect(); window.removeEventListener("resize", syncBounds); };
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const syncKeyboard = () => {
      const editing = document.activeElement?.matches("input, textarea, select, [contenteditable='true']");
      const open = editing && window.innerHeight - viewport.height > 150 && viewport.scale === 1;
      shellRef.current?.classList.toggle("clone-keyboard-open", Boolean(open));
    };
    viewport.addEventListener("resize", syncKeyboard);
    document.addEventListener("focusin", syncKeyboard);
    document.addEventListener("focusout", syncKeyboard);
    return () => {
      viewport.removeEventListener("resize", syncKeyboard);
      document.removeEventListener("focusin", syncKeyboard);
      document.removeEventListener("focusout", syncKeyboard);
    };
  }, []);

  return <div ref={shellRef} className="clone-app-shell">
    <a className="clone-skip-link" href="#clone-main">본문으로 바로가기</a>
    <div className="clone-app-layout">
      <BrandPanel />
      <div ref={panelRef} className="clone-app-panel">
        <div className="clone-site min-h-screen flex flex-col bg-white">
          <AppNavbar />
          <main id="clone-main" className="clone-main flex-1" data-route={pathname} tabIndex={-1}>{children}</main>
          <Footer />
          <BottomNav />
        </div>
      </div>
    </div>
  </div>;
}
